/**
 * StateLayer.tsx — dissolved state choropleth for the national zoom-out LOD tier (T-098)
 *
 * The tier ABOVE CountyLayer: at national zoom (z<5) the map can't ship thousands of
 * counties or tens of thousands of tracts, so this renders ~50 dissolved state polygons
 * (GET /api/states/bbox, backed by the state_choropleth materialized view, T-096). Same
 * choropleth colour ramp on `lvi` as the county and tract layers so the proprietary score
 * reads consistently across every zoom level.
 *
 * Lifecycle mirrors CountyLayer (T-012 W2 pattern): the source + layers are added ONCE and
 * kept mounted; a `visible` prop toggles layout visibility rather than tearing the source
 * down on every zoom-tier switch (no add/removeSource churn). The bbox fetch only runs
 * while visible, so the state tier costs nothing when a finer tier is active.
 *
 * Transport is bbox-GeoJSON for now (mirrors the county tier); the vector-tile pyramid for
 * the low-zoom tiers is a separate transport track (T-087 §3 / T-097) and does not change
 * this component's contract.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { authedFetch } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'
import { stateLabel } from '../../lib/stateNames'

export interface StateLayerProps {
  /** Current viewport bbox as [west, south, east, north]. null before map loads. */
  bbox: [number, number, number, number] | null
  /** Show/hide the layer (driven by the active LOD tier). Default on. */
  visible?: boolean
}

const SOURCE_ID = 'states'
const LAYER_FILL_ID = 'states-fill'
const LAYER_OUTLINE_ID = 'states-outline'

// Burst-resilience tuning — identical to CountyLayer (BEAD-0030): a rapid zoom/pan fires
// 'moveend' many times; debouncing means only the FINAL settled extent is fetched, and a
// transient 5xx self-heals via retry-with-backoff rather than leaving the tier blank.
const DEBOUNCE_MS = 200
const MAX_RETRIES = 2
const BACKOFF_BASE_MS = 250

/** Resolves after `ms`, or rejects with an AbortError if `signal` aborts first. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
    const t = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(t)
        reject(new DOMException('Aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

/**
 * Fetch the state FeatureCollection for a bbox, retrying transient 5xx responses with a
 * linear backoff. A 4xx (client error) is NOT retried — only server saturation (5xx) is
 * treated as transient. Aborting the signal ends the loop immediately.
 */
async function fetchStatesInBbox(
  bbox: [number, number, number, number],
  signal: AbortSignal,
): Promise<GeoJSON.FeatureCollection> {
  const [west, south, east, north] = bbox
  const params = new URLSearchParams({
    south: String(south),
    west: String(west),
    north: String(north),
    east: String(east),
  })
  for (let attempt = 0; ; attempt++) {
    const r = await authedFetch(`/api/states/bbox?${params}`, { signal })
    if (r.ok) return r.json() as Promise<GeoJSON.FeatureCollection>
    if (r.status >= 500 && attempt < MAX_RETRIES) {
      await sleep(BACKOFF_BASE_MS * (attempt + 1), signal)
      continue
    }
    throw new Error(`states/bbox ${r.status}`)
  }
}

export function StateLayer({ bbox, visible = true }: StateLayerProps): null {
  const map = useMap()
  const initializedRef = useRef(false)

  // One-time: add source + fill + outline + hover tooltip.
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    // Same colour stops as CountyLayer / TractLayer's choropleth (score meaning is identical).
    map.addLayer({
      id: LAYER_FILL_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'lvi'],
          0,
          '#ef4444',
          35,
          '#f97316',
          45,
          '#f59e0b',
          60,
          '#4ade80',
          70,
          '#15803d',
        ],
        'fill-opacity': 0.6,
      },
    })

    map.addLayer({
      id: LAYER_OUTLINE_ID,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': '#ffffff',
        'line-width': 0.8,
        'line-opacity': 0.6,
      },
    })

    const hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10,
    })

    // T-392: `mousemove`, NOT `mouseenter`. MapLibre's enter/leave pair is LAYER-scoped, so
    // moving from one polygon into the adjacent one never leaves this fill layer and neither
    // event fires — the popup keeps showing whichever feature the pointer first entered, until
    // it exits the layer entirely. Reproduced on the real state pyramid: a 27-step sweep across
    // the PA/NJ border produced exactly ONE mouseenter while the feature underneath changed.
    let shownKey: string | null = null
    let added = false

    const handleMove = (e: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer'
      const f = e.features?.[0]
      if (!f?.properties) return
      const p = f.properties as Record<string, unknown>
      const score = Math.round(Number(p['lvi'] ?? 0))
      // The state endpoint carries state_abbrev (e.g. "NJ") — the choropleth MV has no full
      // state name — so T-380 resolves it client-side; an unknown code degrades to the raw
      // abbrev rather than a blank line. Same shape as CountyLayer's name line.
      const name = stateLabel(String(p['state_abbrev'] ?? '')) || 'State'

      // Position tracks the cursor on every move; the HTML is rebuilt only when the feature
      // under it actually changes, so identical markup is not re-parsed ~60x/second.
      hoverPopup.setLngLat(e.lngLat)
      const key = `${name}|${score}`
      if (key !== shownKey) {
        shownKey = key
        hoverPopup.setHTML(
          `<div style="font-family:Inter,sans-serif;font-size:12px;padding:2px 0">
            <span style="font-weight:700;font-size:16px;color:var(--foreground)">${score}</span>
            <span style="color:var(--muted-foreground);margin-left:4px">Opportunity Score</span><br/>
            <span style="color:var(--muted-foreground);font-size:11px">${name}</span>
          </div>`,
        )
      }
      if (!added) {
        hoverPopup.addTo(map)
        added = true
      }
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
      shownKey = null
      added = false
      hoverPopup.remove()
    }

    map.on('mousemove', LAYER_FILL_ID, handleMove)
    map.on('mouseleave', LAYER_FILL_ID, handleLeave)

    return () => {
      initializedRef.current = false
      hoverPopup.remove()
      map.off('mousemove', LAYER_FILL_ID, handleMove)
      map.off('mouseleave', LAYER_FILL_ID, handleLeave)
      // BEAD-0037: on sign-out the whole map subtree unmounts and MapView's map.remove()
      // runs first, dropping the style. getLayer/removeLayer would then throw and blank the
      // app. Our teardown is redundant once the map is removed, so skip it if the map is gone.
      if (isMapAlive(map)) {
        if (map.getLayer(LAYER_OUTLINE_ID)) map.removeLayer(LAYER_OUTLINE_ID)
        if (map.getLayer(LAYER_FILL_ID)) map.removeLayer(LAYER_FILL_ID)
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
      }
    }
  }, [map])

  // Visibility toggle (LOD tier switch) — show/hide fill + outline.
  useEffect(() => {
    if (!map || !initializedRef.current) return
    const vis = visible ? 'visible' : 'none'
    if (map.getLayer(LAYER_FILL_ID)) map.setLayoutProperty(LAYER_FILL_ID, 'visibility', vis)
    if (map.getLayer(LAYER_OUTLINE_ID)) map.setLayoutProperty(LAYER_OUTLINE_ID, 'visibility', vis)
  }, [map, visible])

  // Fetch + update source data on bbox change — ONLY while visible (no cost when a finer
  // tier is active). The fetch is DEBOUNCED so a rapid zoom/pan burst collapses to one
  // request for the final extent, and retried with backoff on a transient 5xx. The
  // AbortController cancels a stale in-flight request AND ends any pending retry/debounce.
  useEffect(() => {
    if (!map || !bbox || !visible) return

    const controller = new AbortController()
    const { signal } = controller

    const timer = setTimeout(() => {
      fetchStatesInBbox(bbox, signal)
        .then((data) => {
          if (signal.aborted) return
          const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
          source?.setData(data)
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name !== 'AbortError') console.error(err)
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [map, bbox, visible])

  return null
}
